import { useGlobalContext } from '~/context/GlobalContext';
import { useViewportResize } from '~/hooks/useViewportResize';
import { useEffect, useRef, useState } from 'react';
import {
  CameraIcon, CameraMuteIcon,
  CloseIcon, ResizeIcon,
  CallingTelephoneIcon, TelephoneCancelIcon, TelephoneAcceptIcon,
  MicrophoneIcon, MicrophoneMuteIcon,
  VoiceIcon, VoiceMuteIcon
} from '../Icons/Icons';
import { ICallSession, IWebRTCData } from '~/types/common';
import { useWebRTC } from '~/hooks/useWebRTC';
import styles from './VideoContainer.module.css';
import classNames from 'classnames';

type VideoState = 'closed' | 'full' | 'minimized';

function VideoContainer() {
  const { context, setContext } = useGlobalContext();
  const resize = useViewportResize();
  const { localVideoRef, remoteVideoRef, startCall, cleanup } = useWebRTC();

  const callingRef = useRef<HTMLDivElement>(null);
  const incomingCallRef = useRef<HTMLDivElement>(null);
  const activeCallRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const viewState = useRef<VideoState>('closed');
  const position = useRef({ x: window.innerWidth - 180, y: 100 });
  const isDragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  // Najświeższa sesja połączenia — czytana w sprzątaniu przy odmontowaniu (opuszczenie czatu).
  const callSessionRef = useRef(context.callSession);
  callSessionRef.current = context.callSession;

  // --- STANY DLA MEDIÓW I WIDOCZNOŚCI ---
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isCallActive, setIsCallActive] = useState(false); // Nowy stan do pokazywania przycisków na górnej belce

  const partnerName = context.room?.users?.find(u => u.id !== context.user?.id)?.name;
  const partnerId = context.room?.users?.find(u => u.id !== context.user?.id)?.id;
  const isRoomPrivate = !!context.room?.private;
  const isPendingRoom = context.room?.isPendingRoom;

  const resetOverlays = () => {
    if (callingRef.current) callingRef.current.classList.remove(styles.show);
    if (incomingCallRef.current) incomingCallRef.current.classList.remove(styles.show);
    if (activeCallRef.current) activeCallRef.current.classList.remove(styles.show);
    setIsCallActive(false); // Ukrywamy przyciski na górnej belce
  };

  const showActiveCallControls = () => {
    if (activeCallRef.current) activeCallRef.current.classList.add(styles.show);
    setIsCallActive(true); // Pokazujemy przyciski na górnej belce
  };

  const resetMediaStates = () => {
    setIsMicOn(true);
    setIsCameraOn(true);
    setIsSpeakerOn(true);
  };

  const onOffer = () => {
    resetOverlays();
    showActiveCallControls();
    if (viewState.current === 'closed') { viewState.current = 'full'; updateUI(); }
  };

  const stopCallingSound = () => {
    window.dispatchEvent(new CustomEvent('stopCallingSound', { detail: false }));
  }

  const stopIncomingCallSound = (isIncomingCall?: boolean) => {
    window.dispatchEvent(new CustomEvent('stopIncomingCallSound', { detail: false }));
  }

  const updateUI = () => {
    const el = containerRef.current;
    if (!el) return;

    el.className = `${styles.videoContainer} ${styles[viewState.current]}`;

    if (viewState.current === 'minimized') {
      el.style.left = `${position.current.x}px`;
      el.style.top = `${position.current.y}px`;
      el.style.right = 'auto';
    } else {
      el.style.left = ''; el.style.top = ''; el.style.right = '';
    }
  };

  const toggleMic = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (localVideoRef.current && localVideoRef.current.srcObject) {
      const stream = localVideoRef.current.srcObject as MediaStream;
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length > 0) {
        audioTracks[0].enabled = !isMicOn;
        setIsMicOn(!isMicOn);
      }
    }
  };

  const toggleCamera = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (localVideoRef.current && localVideoRef.current.srcObject) {
      const stream = localVideoRef.current.srcObject as MediaStream;
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();

      const newCameraState = !isCameraOn;

      if (videoTracks.length > 0) {
        videoTracks[0].enabled = newCameraState;
      }
      setIsCameraOn(newCameraState);

      if (audioTracks.length > 0) {
        audioTracks[0].enabled = newCameraState;
        setIsMicOn(newCameraState);
      }
    }
  };

  const toggleSpeaker = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (remoteVideoRef.current) {
      remoteVideoRef.current.muted = isSpeakerOn;
      setIsSpeakerOn(!isSpeakerOn);
    }
  };

  const callToUser = () => {
    resetOverlays();

    const data: ICallSession = {
      event: 'callToUser',
      initiatorId: context.user?.id || '',
      initiatorName: context.user?.name || '',
      partnerId: partnerId || '',
      partnerName: partnerName || '',
      roomId: context.room?.id || '',
      isUserBusy: true,
    };

    setContext({ callSession: data });

    window.dispatchEvent(new CustomEvent('webRTC', { detail: data }));
    window.dispatchEvent(new CustomEvent('startRingingOut'));

    if (callingRef.current) callingRef.current.classList.add(styles.show);
  };

  const handleUserIsBusy = (e: any) => {
    console.log('handleUserIsBusy: ', e);
    try { cleanup(); } catch (e) { console.error(e); }
    resetOverlays();
    resetMediaStates();
    viewState.current = 'closed';
    updateUI();
    setContext({ callSession: null });
  };

  const handleIncomingCall = (e: any) => {
    const incomingData = e.detail as ICallSession;

    if (context.callSession) {
      const busyData: ICallSession = {
        event: 'userIsBusy',
        initiatorId: incomingData.initiatorId,
        initiatorName: incomingData.initiatorName,
        partnerId: incomingData.partnerId,
        partnerName: incomingData.partnerName,
        roomId: incomingData.roomId,
        isUserBusy: true,
      };
      window.dispatchEvent(new CustomEvent('webRTC', { detail: busyData }));
      // stopIncomingCallSound();
      return;
    }

    setContext({
      callSession: {
        event: 'userCalling',
        initiatorId: incomingData.initiatorId,
        initiatorName: incomingData.initiatorName,
        partnerId: incomingData.partnerId,
        partnerName: incomingData.partnerName,
        roomId: incomingData.roomId,
        isUserBusy: true,
      }
    });

    resetOverlays();
    window.dispatchEvent(new CustomEvent('startCallingComing'));

    if (incomingCallRef.current) incomingCallRef.current.classList.add(styles.show);
    if (viewState.current === 'closed') {
      viewState.current = 'full';
      updateUI();
    }
  };

  const onCancelCall = () => {
    try { cleanup(); } catch (e) { console.error(e); }
    resetOverlays();
    resetMediaStates();
    stopCallingSound();
    viewState.current = 'closed';
    updateUI();

    const data: IWebRTCData = {
      event: 'cancelCall',
      initiatorId: context.callSession?.initiatorId || '',
      partnerId: context.callSession?.partnerId || '',
      roomId: context.callSession?.roomId || '',
      session: null
    };

    window.dispatchEvent(new CustomEvent('webRTC', { detail: data }));
    setContext({ callSession: null });

  };

  const handleCancelCall = () => {
    try { cleanup(); } catch (e) { console.error(e); }
    resetOverlays();
    resetMediaStates();
    stopCallingSound();
    viewState.current = 'closed';
    updateUI();
    setContext({ callSession: null });
  };

  const handleToggleVideo = async () => {
    if (viewState.current === 'closed') {
      viewState.current = 'full';
      updateUI();
      callToUser();
    } else {
      onCancelCall();
    }
  };

  const handleAcceptCall = async () => {
    resetOverlays();
    resetMediaStates();
    showActiveCallControls();
    stopCallingSound();

    const data: ICallSession = {
      event: 'callSessionAccept',
      initiatorId: context.callSession?.initiatorId || '',
      initiatorName: context.callSession?.initiatorName || '',
      partnerId: context.callSession?.partnerId || '',
      partnerName: context.callSession?.partnerName || '',
      roomId: context.callSession?.roomId || '',
    };

    window.dispatchEvent(new CustomEvent('callSessionAccept', { detail: data }));
    await startCall();
  };

  const callSessionAccepted = (e: any) => {
    const incomingData = e.detail as ICallSession;
    stopCallingSound();
    setContext({
      callSession: {
        event: 'callSessionAccepted',
        initiatorId: incomingData.initiatorId,
        initiatorName: incomingData.initiatorName,
        partnerId: incomingData.partnerId,
        partnerName: incomingData.partnerName,
        roomId: incomingData.roomId,
        isUserBusy: true,
      }
    });
  }

  const handleResize = () => {
    viewState.current = viewState.current === 'full' ? 'minimized' : 'full';
    updateUI();
  };

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    if (viewState.current !== 'minimized') return;
    isDragging.current = true;
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
    offset.current = { x: clientX - position.current.x, y: clientY - position.current.y };
  };

  const handleMove = (e: MouseEvent | TouchEvent) => {
    if (!isDragging.current || viewState.current !== 'minimized') return;
    const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
    position.current = {
      x: Math.max(0, Math.min(window.innerWidth - 160, clientX - offset.current.x)),
      y: Math.max(0, Math.min(window.innerHeight - 220, clientY - offset.current.y))
    };
    if (containerRef.current) {
      containerRef.current.style.left = `${position.current.x}px`;
      containerRef.current.style.top = `${position.current.y}px`;
    }
  };

  const handleUp = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    window.addEventListener('userCalling', handleIncomingCall);

    return () => {
      window.removeEventListener('userCalling', handleIncomingCall);
    }
  }, [context.callSession]);

  useEffect(() => {
    window.addEventListener('cancelCall', handleCancelCall);
    window.addEventListener('userIsBusy', handleUserIsBusy);
    window.addEventListener('webRtcOffer', onOffer);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleUp);
    window.addEventListener('webRtcAnswer', stopCallingSound);
    window.addEventListener('callSessionAccepted', callSessionAccepted);

    return () => {
      window.removeEventListener('userCalling', handleIncomingCall);
      window.removeEventListener('userIsBusy', handleUserIsBusy);
      window.removeEventListener('webRtcOffer', onOffer);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleUp);
      window.removeEventListener('webRtcAnswer', stopCallingSound);
      window.removeEventListener('callSessionAccepted', callSessionAccepted);
    };
  }, []);

  // Opuszczenie czatu (np. przejście na ruletkę) z aktywną rozmową prywatną — także ze
  // zminimalizowanym oknem wideo — musi ją zerwać: gasimy kamerę, zamykamy połączenie P2P
  // i powiadamiamy partnera, żeby jego strona też się rozłączyła. Dzięki temu ruletka
  // startuje w trybie domyślnym (szukanie nowej osoby), bez wiszącego połączenia w tle.
  useEffect(() => {
    return () => {
      const session = callSessionRef.current;
      if (!session || session.event === 'rouletteSession') return;

      try { cleanup(); } catch (e) { console.error(e); }

      window.dispatchEvent(new CustomEvent('webRTC', {
        detail: {
          event: 'cancelCall',
          initiatorId: session.initiatorId,
          partnerId: session.partnerId,
          roomId: session.roomId,
          session: null,
        } as IWebRTCData,
      }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCallButton = () => {
    if (!context?.room?.private) return;
    if (context?.room?.isPendingRoom) return;
    if (context.callSession !== null) return;
    if (context.activeSidebarId !== '') return;

    return (
      <button
        className={styles.trigger}
        onClick={handleToggleVideo}>
        <CameraIcon />
      </button>
    );
  }

  return (
    <div
      ref={containerRef}
      className={classNames(
        styles.videoContainer,
        styles[viewState.current],
      )}>

      {/* --- ZAKTUALIZOWANA GÓRNA BELKA --- */}
      <div className={styles.controls}>
        {renderCallButton()}

        {/* Kontrolki pokazują się tylko podczas aktywnej rozmowy */}
        {isCallActive && (
          <>
            <button className={styles.iconBtn} onClick={toggleSpeaker}>
              {isSpeakerOn ? <VoiceIcon /> : <VoiceMuteIcon />}
            </button>
            <button className={styles.iconBtn} onClick={toggleMic}>
              {isMicOn ? <MicrophoneIcon /> : <MicrophoneMuteIcon />}
            </button>
            <button className={styles.iconBtn} onClick={toggleCamera}>
              {isCameraOn ? <CameraIcon /> : <CameraMuteIcon />}
            </button>
          </>
        )}

        {/* <button className={styles.iconBtn} onClick={onCancelCall}><CloseIcon /></button> */}
        <button className={styles.iconBtn} onClick={handleResize}><ResizeIcon /></button>
      </div>

      <div className={styles.videoWrapper} onMouseDown={handleMouseDown} onTouchStart={handleMouseDown}>
        <video ref={remoteVideoRef} className={styles.remoteVideo} playsInline autoPlay />

        {/* NAKŁADKA 1: Ja dzwonię (Oczekiwanie) */}
        <div ref={callingRef} className={styles.callingWrapper}>
          <div className={styles.callingLoader} />
          <div className={styles.callingText}>Dzwonisz do {context.callSession?.partnerName}</div>
          <div className={styles.buttonContainer}>
            <div className={styles.cancelButton} onClick={onCancelCall}><TelephoneCancelIcon className={styles.telephoneCancelIcon} /></div>
          </div>
        </div>

        {/* NAKŁADKA 2: Ktoś dzwoni do mnie */}
        <div ref={incomingCallRef} className={styles.incomingCallWrapper}>
          <div className={styles.pulseLoader} />
          <div className={styles.callingText}>Dzwoni {context.callSession?.initiatorName}</div>
          <div className={classNames(styles.buttonContainer, styles.buttonContainerIncoming)}>
            <div className={styles.acceptButton} onClick={handleAcceptCall}><TelephoneAcceptIcon className={styles.telephoneAcceptIcon} /></div>
            <div className={styles.cancelButton} onClick={onCancelCall}><TelephoneCancelIcon className={styles.telephoneCancelIcon} /></div>
          </div>
        </div>

        {/* NAKŁADKA 3: AKTYWNA ROZMOWA (Została tu tylko czerwona słuchawka) */}
        <div ref={activeCallRef} className={classNames(styles.activeCallWrapper, styles.buttonContainer)}>
          <div className={styles.cancelButton} onClick={onCancelCall}>
            <TelephoneCancelIcon className={styles.telephoneCancelIcon} />
          </div>
        </div>

        <div className={styles.localVideoContainer}>
          <video ref={localVideoRef} className={styles.localVideo} playsInline autoPlay muted />
        </div>
      </div>
    </div>
  );
}

export default VideoContainer;